import { original, produce } from 'immer';
import { cloneDeep } from 'lodash';
import get from 'lodash/get';
import {
  initialLayout,
  normalizeToLayoutSlug,
} from 'pages/Dashboard/Overview/helpers';
import { assoc } from 'ramda';
import {
  ADD_LAYOUT,
  ADD_ROW,
  ADD_WIDGET,
  CLEAR_DASHBOARD,
  CREATE_REPORT_GROUP,
  DELETE_LAYOUT,
  DELETE_REPORT_GROUP,
  FETCH_DASHBOARD,
  FETCH_DISCOVERY_HISTORY,
  FETCH_HOST_VENDOR_MIX,
  FETCH_NETWORK_VENDOR_MIX,
  FETCH_REPORTS,
  FETCH_TEXT_ROW,
  MOVE_COLUMN,
  MOVE_WIDGET,
  REMOVE_ROW,
  REMOVE_WIDGET,
  RENAME_LAYOUT,
  SAVE_LAYOUT,
  SORT_COLUMNS,
  SORT_WIDGETS,
  SWITCH_ACTIVE_LAYOUT,
  SWITCH_EDIT_LAYOUT_MODE,
  UPDATE_COLUMN_PROPERTY,
  UPDATE_REPORT_GROUP,
  UPDATE_WIDGET,
} from './constants';

export const initialState = {
  dashboardLoading: false,
  layout: initialLayout,
  layouts: [],
  layoutsDict: {},
  leaveDialogue: { open: false, canLeave: false, pathname: null },
  reports: null,
  reportsGroups: null,
  reportsGroupsBeforeUpdate: null,
  dataModified: false,
  selectedGroup: null,
  requestOperations: [],
  discoveryHistory: [],
  networkVendorMix: {},
  hostVendorMix: {},
  textRow: {},
  isEditLayoutMode: false,
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
const dashboardOverviewReducer = produce((draft, action) => {
  switch (action.type) {
    case `${FETCH_DASHBOARD}_START`:
      draft.dashboardLoading = true;
      break;
    case `${FETCH_DASHBOARD}_ERROR`:
      draft.dashboardLoading = false;
      break;
    case `${FETCH_DASHBOARD}_SUCCESS`: {
      const { reports, reportsGroups, layout, layouts, layoutsDict } =
        action.payload;
      draft.reports = reports;
      draft.reportsGroups = reportsGroups;
      if (layout && get(layout, ['rows', 0])) {
        draft.layout = layout;
      }
      draft.layouts = layouts;
      draft.layoutsDict = layoutsDict;
      draft.dataModified = false;
      draft.dashboardLoading = false;
      break;
    }
    case SWITCH_ACTIVE_LAYOUT: {
      const { layoutSlug } = action.payload;
      draft.layout = draft.layoutsDict[layoutSlug];
      break;
    }
    case ADD_LAYOUT: {
      const { layoutTitle } = action.payload;

      const newLayout = { ...initialLayout };
      newLayout.title = layoutTitle;
      newLayout.slug = normalizeToLayoutSlug(layoutTitle);

      draft.layouts.push(newLayout);
      draft.layoutsDict[newLayout.slug] = newLayout;
      draft.layout = newLayout;
      draft.isEditLayoutMode = true;
      break;
    }
    case `${RENAME_LAYOUT}_START`: {
      const { layout, layouts, layoutsDict } = action.payload;

      draft.layout = layout;
      draft.layouts = layouts;
      draft.layoutsDict = layoutsDict;
      break;
    }
    case `${DELETE_LAYOUT}_START`: {
      const { layout, layouts, layoutsDict } = action.payload;

      draft.layout = layout;
      draft.layouts = layouts;
      draft.layoutsDict = layoutsDict;
      break;
    }
    case SWITCH_EDIT_LAYOUT_MODE: {
      const { editMode } = action.payload;
      draft.isEditLayoutMode = editMode;
      break;
    }
    case `${FETCH_DISCOVERY_HISTORY}_SUCCESS`: {
      draft.discoveryHistory = (
        action.payload.discoveryHistory || []
      ).reverse();
      break;
    }
    case `${FETCH_NETWORK_VENDOR_MIX}_SUCCESS`: {
      draft.networkVendorMix = action.payload.networkVendorMix;
      break;
    }
    case `${FETCH_HOST_VENDOR_MIX}_SUCCESS`: {
      draft.hostVendorMix = action.payload.hostVendorMix;
      break;
    }
    case `${FETCH_TEXT_ROW}_SUCCESS`: {
      draft.textRow = get(action, ['payload', 'data', 'data', 0], {});
      break;
    }
    case `${SAVE_LAYOUT}_SUCCESS`: {
      const { layout } = action.payload;

      // When "initialLayout" is the only layout present, slug and title are not present.
      if (layout.slug) {
        draft.layoutsDict[layout.slug] = layout;
        draft.layout = layout;
      }

      draft.isEditLayoutMode = false;
      draft.dataModified = false;
      draft.requestOperations = [];
      break;
    }
    case UPDATE_REPORT_GROUP: {
      const { group } = action.payload;

      draft.reportsGroupsBeforeUpdate = cloneDeep(draft.reportsGroups);

      const foundIndex = draft.reportsGroups.findIndex(
        (g) => g.id === group.id,
      );
      if (foundIndex > -1) {
        draft.reportsGroups[foundIndex] = group;
        draft.requestOperations.push({ type: 'update', data: group });
      }
      break;
    }
    case `${CREATE_REPORT_GROUP}_SUCCESS`: {
      const { group, values, rowIndex, columnIndex, widgetName } =
        action.payload;
      draft.layout.rows[rowIndex].columns[columnIndex].widgets.push({
        key: widgetName,
        props: assoc('group', group.id, values),
      });
      draft.reportsGroups.push(group);
      draft.selectedGroup = group.id;
      draft.dataModified = true;
      break;
    }
    case `${DELETE_REPORT_GROUP}_SUCCESS`: {
      const { groupId } = action.payload;
      const foundIndex = draft.reportsGroups.findIndex(
        (group) => group.id === groupId,
      );
      if (foundIndex > -1) {
        draft.reportsGroups.splice(foundIndex, 1);
      }
      break;
    }
    case `${FETCH_REPORTS}_SUCCESS`: {
      const { reports } = action.payload;
      draft.reports = reports;
      break;
    }
    case `${CLEAR_DASHBOARD}_SUCCESS`: {
      for (const key of Object.keys(draft)) {
        if (initialState[key]) {
          draft[key] = initialState[key];
        }
      }
      break;
    }
    case UPDATE_COLUMN_PROPERTY: {
      const { rowIndex, columnIndex, property, value } = action.payload;
      draft.layout.rows[rowIndex].columns[columnIndex][property] = value;
      break;
    }
    case ADD_ROW: {
      const { rowIndex, row } = action.payload;
      draft.layout.rows.splice(rowIndex, 0, row);
      break;
    }
    case REMOVE_ROW: {
      const { rowIndex } = action.payload;
      draft.layout.rows.splice(rowIndex, 1);
      break;
    }
    case REMOVE_WIDGET: {
      const { rowIndex, columnIndex, widgetIndex } = action.payload;
      draft.layout.rows[rowIndex].columns[columnIndex].widgets.splice(
        widgetIndex,
        1,
      );
      break;
    }
    case `${ADD_WIDGET}_SUCCESS`: {
      const { rowIndex, columnIndex, widgetName, props } = action.payload;
      draft.layout.rows[rowIndex].columns[columnIndex].widgets.push({
        key: widgetName,
        props,
      });
      draft.dataModified = true;
      break;
    }
    case UPDATE_WIDGET: {
      const { rowIndex, columnIndex, widgetIndex, props } = action.payload;
      draft.layout.rows[rowIndex].columns[columnIndex].widgets[
        widgetIndex
      ].props = props;
      break;
    }
    case SORT_WIDGETS: {
      const { initialLocation, destination, widgetName, widgetProps } =
        action.payload;
      draft.layout.rows[initialLocation.rowIndex].columns[
        initialLocation.columnIndex
      ].widgets.splice(initialLocation.widgetIndex, 1);
      draft.layout.rows[initialLocation.rowIndex].columns[
        initialLocation.columnIndex
      ].widgets.splice(destination.widgetIndex, 0, {
        key: widgetName,
        props: widgetProps,
      });
      break;
    }
    case MOVE_WIDGET: {
      const { initialLocation, destination, widgetName, widgetProps } =
        action.payload;
      draft.layout.rows[initialLocation.rowIndex].columns[
        initialLocation.columnIndex
      ].widgets.splice(initialLocation.widgetIndex, 1);
      draft.layout.rows[destination.rowIndex].columns[
        destination.columnIndex
      ].widgets.push({ key: widgetName, props: widgetProps });
      break;
    }
    case SORT_COLUMNS: {
      const { initialLocation, destination } = action.payload;
      const initialColumn = original(
        draft.layout.rows[initialLocation.rowIndex].columns[
          initialLocation.columnIndex
        ],
      );
      draft.layout.rows[initialLocation.rowIndex].columns.splice(
        initialLocation.columnIndex,
        1,
      );
      draft.layout.rows[destination.rowIndex].columns.splice(
        destination.columnIndex,
        0,
        initialColumn,
      );
      break;
    }
    case MOVE_COLUMN: {
      const { initialLocation, destination } = action.payload;
      const columnToMove =
        draft.layout.rows[initialLocation.rowIndex].columns[
          initialLocation.columnIndex
        ];
      draft.layout.rows[initialLocation.rowIndex].columns.splice(
        initialLocation.columnIndex,
        1,
      );
      const destinationColumnsLength = original(
        draft.layout.rows[destination.rowIndex].columns,
      ).length;
      draft.layout.rows[destination.rowIndex].columns.splice(
        destinationColumnsLength,
        0,
        columnToMove,
      );
      for (const column of draft.layout.rows[destination.rowIndex].columns) {
        column.className = `col-xs-12 col-lg${12 / destinationColumnsLength + 1}`;
      }
      if (!draft.layout.rows[initialLocation.rowIndex].columns.length) {
        draft.layout.rows.splice(initialLocation.rowIndex);
      } else {
        const initialColumnsLength = original(
          draft.layout.rows[initialLocation.rowIndex].columns,
        ).length;
        for (const column of draft.layout.rows[initialLocation.rowIndex]
          .columns) {
          column.className = `col-xs-12 col-lg${12 / initialColumnsLength}`;
        }
      }
      break;
    }
  }
}, initialState);

export default dashboardOverviewReducer;
